import { UITable } from '../../components/table/ui-table';
import type { ComponentPlugin } from '../../components/ui-element.type';
import { setUnsafeInnerHTML, updateClassList } from '../../global/ui-helpers';
import { Digest } from '../../global/index';

/**
 * @class
 * @memberof SharedComponents
 * @classdesc Class for ui-table comparison plugin.
 * @alias UITableComparison
 * @implements {ComponentPlugin}
 * @param {HTMLTableElement} target Target instance.
 */
export class UITableComparison implements ComponentPlugin {
    private readonly target: UITable;

    private scrollHandler!: (e: Event) => void;

    constructor(target: UITable) {
        this.target = target;
    }

    /**
     * @type {number}
     */
    static readonly ColumnsCountToShrink: number = 3;

    /**
     * Builds comparison table, transform markup.
     * @private
     */
    private prepareMarkup(): void {
        const caption = this.target.table.querySelector('caption');
        let colgroup = this.target.table.querySelector('colgroup');
        let colspan = 0;
        let rows: NodeListOf<HTMLTableRowElement>;

        // This only adds ids to header cells of a first row if there are multiple header rows, to provide backward compatibility for when there was only one row.
        // The first row is considered the label row and must not have complex content. This is to ensure that screen readers read the headers correctly.
        // If there are multiple header rows, the additional rows are assumed to not label the columns, so they are allowed to have complex content, but must use td tag instead of th.
        const tHeadLabelCells = this.target.table.tHead?.querySelectorAll(
            'tr:first-child th[scope="col"]'
        );
        for (let i = 0; i < (tHeadLabelCells || []).length; i++) {
            tHeadLabelCells![i].id ||= `${Digest.randomId()}-table-header-${i}`;
        }
        // even the top left cell should be wrapped in a container
        // Choose a cell that does not contain only .-visually-hidden, as well as the first cell that is followed
        // note that nesting :has is forbidden
        const richHeaderCells = this.target.table.tHead?.querySelectorAll(
            ':is(td, th):not(:has(>.-visually-hidden:only-child)):not(:first-child:has(~ :is(td, th) > .-visually-hidden:only-child))'
        );
        for (let i = 0; i < (richHeaderCells || []).length; i++) {
            const container = this.target.createElement({
                tagName: 'div',
                classList: {
                    'table-header-container': true,
                },
                children: [...richHeaderCells![i].childNodes],
            });
            richHeaderCells![i].appendChild(container);
        }

        const bodiesAndFooter = [
            ...this.target.table.tBodies,
            this.target.table.tFoot,
        ].filter((x) => !!x);
        for (const body of bodiesAndFooter) {
            rows = body.querySelectorAll('tr');
            colspan = this.countColumns(rows);
            let rowExpandId = '';
            for (const [i, row] of rows.entries()) {
                const isSubRow = row.classList.contains('-sub');
                const isExpandRow = row.classList.contains('-expand');
                const isExtraRow = row.classList.contains('-extra');
                const isOpenRow = row.classList.contains('-open');
                const isFooterRow = row.closest('tfoot') !== null;
                if (isExpandRow) {
                    rowExpandId =
                        row.cells[0].id || `${Digest.randomId()}-row-${i}`;
                    row.cells[0].id = rowExpandId;
                }
                const rowElement = this.target.createElement({
                    tagName: 'tr',
                    classList: {
                        'row': true,
                        '-fixed': true,
                        '-sub': isSubRow,
                        '-expand': isExpandRow,
                        '-extra': isExtraRow,
                        '-open': isOpenRow,
                    },
                    children: [
                        {
                            tagName: 'td',
                            attributes: {
                                colspan: colspan - 1,
                                headers: rowExpandId,
                            },
                            children: [
                                this.target.createElement({
                                    tagName: 'div',
                                    classList: {
                                        'fixed-title': true,
                                    },
                                }),
                            ],
                        },
                    ],
                });
                const titleElement = rowElement.querySelector('.fixed-title');
                if (titleElement) {
                    setUnsafeInnerHTML(titleElement, row.cells[0].innerHTML);
                }
                if (!isFooterRow) {
                    row.insertAdjacentElement('beforebegin', rowElement);
                }
                if (!isExpandRow || isFooterRow) {
                    for (let j = 0; j < row.cells.length; j++) {
                        const id = `${Digest.randomId()}-${isFooterRow ? 'footerheader' : 'subheader'}-${i}-${j}`;
                        row.cells[j].id ||= id;
                        if (row.cells[j].localName === 'td') {
                            row.cells[j].setAttribute(
                                'headers',
                                [
                                    tHeadLabelCells?.[j - 1]?.id,
                                    rowExpandId,
                                    row.cells[0].id,
                                ]
                                    .filter((x) => x)
                                    .join(' ')
                            );
                        }
                    }
                }
            }
        }

        if (!colgroup) {
            colgroup = document.createElement('colgroup');
            for (let i = 0; i < colspan; i++) {
                colgroup.appendChild(document.createElement('col'));
            }
        }

        if (!this.target.table.contains(colgroup)) {
            if (caption) {
                caption.insertAdjacentElement('afterend', colgroup);
            } else {
                this.target.table.insertAdjacentElement('afterbegin', colgroup);
            }
        }
    }

    /**
     * Returns total number of columns in table, accounting for colspans.
     * Takes into account colspans.
     * @private
     * @param {NodeListOf<HTMLTableRowElement>} rows - Array of row nodes selected from table body.
     * @returns {number} Maximum number of rows
     */
    private countColumns(rows: NodeListOf<HTMLTableRowElement>): number {
        const children = rows[0]?.children || [];
        return Array.from(children).reduce(
            (total, td) => total + (td as HTMLTableCellElement).colSpan,
            0
        );
    }

    /**
     * Highlights column or columns in comparison table.
     * @param {number | Array<number>} input - Index or indexes of columns to be highlighted.
     */
    highlightColumns(input: number | Array<number>): void {
        if (!input || (Array.isArray(input) && !input.length)) {
            return;
        }
        const cols = this.target.table.querySelectorAll('col');
        for (const [index, col] of cols.entries()) {
            updateClassList(col, {
                '-highlight': Array.isArray(input)
                    ? input.includes(index)
                    : index === input,
            });
        }
    }

    /**
     * Handles comparison expandable table's horizontal scroll.
     * TODO: when CSS will have good position: sticky support remove this and do no-js solution.
     * @param {Event} e
     * @private
     */
    private handleScroll(e: Event): void {
        const stickyDivs =
            this.target.querySelectorAll<HTMLElement>('.fixed-title');
        const BASE_OFFSET = 10;
        for (const div of stickyDivs) {
            div.style.left =
                BASE_OFFSET + (e.target as Element).scrollLeft + 'px';
            div.style.width = window.innerWidth - BASE_OFFSET - 100 + 'px';
        }
    }

    /**
     * Shrink columns on smaller screens to fit columns on screen if
     * column's count is less than COLUMNS_COUNT_TO_SHRINK(3).
     * @private
     */
    private shrinkColumns(): void {
        const colsCount = this.target.querySelectorAll('col').length;
        if (colsCount <= UITableComparison.ColumnsCountToShrink) {
            this.target.table.classList.add('-shrink');
        }
    }

    /**
     * @inheritDoc
     */
    render(): void {
        this.target.table.classList.add('-comparison');
        this.shrinkColumns();
        this.prepareMarkup();
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        this.scrollHandler = this.handleScroll.bind(this);
        this.target.addEventListener('scroll', this.scrollHandler);
        this.target.dispatchEvent(new Event('scroll'));
        window.addEventListener('resize', this.scrollHandler);
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        this.target.removeEventListener('scroll', this.scrollHandler);
    }

    /**
     * @inheritDoc
     */
    reconnect(): void {
        this.target.addEventListener('scroll', this.scrollHandler);
    }
}

UITable.registerPlugin('comparison', UITableComparison);
